import {Store} from 'client/framework';
import notifier from '@albert-io/notifier';
import {isTransportError} from 'lib/normalizeError';
import sessionStore from 'client/Session/SessionStore';
import questionTypeActions from 'components/QuestionTypes/questionType.actions';
import type {List} from 'immutable';
import {Map} from 'immutable';
import {debounce} from 'lodash';
import type {DebouncedFunc} from 'lodash';

import {GuessModelV1} from 'resources/augmented/Guess/GuessModel.v1';
import isGuessOrDraftGuessEmpty from 'resources/extensions/isGuessOrDraftGuessEmpty';

import {getAssignmentIdFromPath} from './assignmentIdFromPath';
import {
  SessionEndedDuringDraftLoadError,
  getDraftGuessWithSessionRecovery,
  shouldReportDraftLoadFailure
} from './draftGuessSessionRecovery';
import {patchDraftGuess, saveDraftGuess} from './QuestionType.queries';
import {SaveStatus} from './QuestionType.types';
import {createEmptyGuess} from './QuestionType.guess';

interface DraftGuessInitialData {
  draftId: string | null;
  draftGuess: unknown;
  draftGuessPromise: Promise<unknown> | null;
  draftLoadFailed: boolean;
  editStartTime: Date | null;
  hasPendingDebouncedSaveDraftGuess: boolean;
  lastDraftTime: string | null;
  questionId: string | null;
  saveStatus: SaveStatus;
  timeElapsed: number;
}

interface BuildGuessOptions {
  assignmentId?: string | null;
  timeElapsed?: number;
  practiceId?: string | null;
  diagnosticId?: string | null;
  content?: unknown;
}

interface GuessData {
  content: unknown;
  time_elapsed: number;
  assignment_id?: string;
  eliminated_options?: unknown;
  adaptive_practice_id?: string;
  diagnostic_id?: string;
}

class QuestionTypeStore extends Store {
  /**
   * These members are implemented by question-type-specific subclasses. They are
   * `declare`d so the base class emits NO runtime field — a plain field with no
   * initializer compiles to `this.x = undefined` in the base constructor and would
   * clobber the subclass prototype methods. The base references them dynamically,
   * guarded by `typeof`/truthiness checks.
   */
  declare afterLoadDraftGuess?: (draftGuess: unknown) => void;

  declare buildGuessContent: () => unknown;

  declare buildDraftGuessContent?: () => unknown;

  declare buildEliminatedOptions?: () => unknown;

  draftGuessInitialData: DraftGuessInitialData;

  debouncedSaveDraftGuess: DebouncedFunc<() => Promise<void>>;

  saveChain: Promise<void> = Promise.resolve();

  constructor(name: string, questionId: string | null = null, saveDraftGuessDebounceWait = 250) {
    super(name);

    this.draftGuessInitialData = {
      draftId: null,
      draftGuess: null,
      draftGuessPromise: null,
      draftLoadFailed: false,
      editStartTime: null,
      hasPendingDebouncedSaveDraftGuess: false,
      lastDraftTime: null,
      questionId,
      saveStatus: SaveStatus.LOADING,
      timeElapsed: 0
    };

    this.handleTargeted(
      questionTypeActions.CANCEL_DEBOUNCED_SAVE_DRAFT_GUESS,
      this.cancelDebouncedSaveDraftGuess,
      this.name
    );
    this.handleTargeted(questionTypeActions.CLEAR_GUESS, this._clearGuess, this.name);
    this.handleTargeted(
      questionTypeActions.CLEAR_DRAFT_GUESS_DATA,
      this._clearDraftGuessData,
      this.name
    );
    this.handleTargeted(questionTypeActions.LOAD_DRAFT_GUESS, this.loadDraftGuess, this.name);
    this.handleTargeted(questionTypeActions.LOAD_GUESS, this._loadGuess, this.name);
    this.handleTargeted(questionTypeActions.SAVE_DRAFT_GUESS, this.saveDraftGuess, this.name);
    this.handleTargeted(questionTypeActions.START_TIMER, this.startTimer, this.name);
    this.handleTargeted(
      questionTypeActions.UPDATE_QUESTION,
      this.setProperty('__question'),
      this.name
    );

    this.debouncedSaveDraftGuess = debounce(
      this.saveDraftGuess.bind(this),
      saveDraftGuessDebounceWait
    );
  }

  buildCorrectGuess(question) {
    return new GuessModelV1({
      content: question.getValidResponse(),
      correct: true,
      points_earned: question.getPoints(),
      question_v3: question
    });
  }

  buildEmptyGuess(question) {
    return createEmptyGuess(question);
  }

  buildGuess({
    assignmentId,
    timeElapsed = 0,
    practiceId,
    diagnosticId,
    content
  }: BuildGuessOptions = {}) {
    if (typeof timeElapsed !== 'number') {
      throw new Error('`timeElapsed` is missing from guess content.');
    }

    const guessData: GuessData = {
      content: content ?? this.buildGuessContent(),
      time_elapsed: this.buildTimeElapsed()
    };

    if (assignmentId) {
      guessData.assignment_id = assignmentId;
    }

    // Only used for Multiple Choice Questions
    if (this.buildEliminatedOptions) {
      guessData.eliminated_options = this.buildEliminatedOptions();
    }

    if (practiceId) {
      guessData.adaptive_practice_id = practiceId;
    }

    if (diagnosticId) {
      guessData.diagnostic_id = diagnosticId;
    }

    return new GuessModelV1(guessData)
      .updateRelationship('question_v3', this.getQuestionId())
      .updateRelationship('student_v2', sessionStore.getUserId())
      .updateRelationship('assignment_v3', assignmentId, !!assignmentId);
  }

  buildTimeElapsed() {
    const editStartTime = this.readData('editStartTime');
    const prevTimeElapsed = this.readData('timeElapsed');
    const now = new Date();

    const newTimeElapsed = editStartTime ? now.getTime() - new Date(editStartTime).getTime() : 0;
    const totalTime = newTimeElapsed + prevTimeElapsed;

    return totalTime;
  }

  cancelDebouncedSaveDraftGuess() {
    this.debouncedSaveDraftGuess.cancel();
    this.writeData('hasPendingDebouncedSaveDraftGuess', false);
  }

  _clearDraftGuessData() {
    this.writeData('draftId', null);
    this.writeData('draftGuess', null);
    this.writeData('draftLoadFailed', false);
    this.writeData('timeElapsed', 0);
    this.writeData('saveStatus', SaveStatus.DEFAULT);
    this.writeData('lastDraftTime', null);
  }

  _clearGuess() {
    this.writeData((store) =>
      store
        .set('isGuessCorrect', false)
        .set('rubric', null)
        .set('solutionText', null)
        .set('hasResponse', false)
        .set('pointsPossible', null)
        .set('pointsEarned', null)
    );

    // After clearing the data, we'll force the window to the top of the page
    setTimeout(() => {
      global.scrollTo(0, 0);
    }, 0);
  }

  static getAssignmentId() {
    return getAssignmentIdFromPath();
  }

  getDraftId() {
    return this.readData('draftId');
  }

  getDraftGuess() {
    return this.readData('draftGuess');
  }

  getDraftGuessPromise() {
    return this.readData('draftGuessPromise');
  }

  getHasDraftGuess() {
    return this.getDraftGuess() !== null;
  }

  getHasPendingDebouncedSaveDraftGuess() {
    return this.readData('hasPendingDebouncedSaveDraftGuess');
  }

  getLastSaveTime(): Date | null {
    const saveTime = this.readData('lastDraftTime');
    return saveTime ? new Date(saveTime) : null;
  }

  getQuestionId = () => this.readData('questionId');

  getQuestion = () => this.readData('__question');

  getResponseGuess = () => this.readData('responseGuess');

  getSaveStatus() {
    return this.readData('saveStatus');
  }

  kickoffDebouncedSaveDraftGuess() {
    if (QuestionTypeStore.shouldNotSaveDraft()) {
      return;
    }

    this.debouncedSaveDraftGuess();
    this.writeData('hasPendingDebouncedSaveDraftGuess', true);
  }

  async loadDraftGuess() {
    if (QuestionTypeStore.shouldNotSaveDraft()) {
      this.writeData('saveStatus', SaveStatus.DEFAULT);
      return;
    }

    if (typeof this.afterLoadDraftGuess !== 'function') {
      throw new Error('afterLoadDraftGuess must be implemented in order to load drafts.');
    }

    const assignmentId = QuestionTypeStore.getAssignmentId();
    const questionId = this.readData('questionId');

    let response: List<Map<string, unknown>> | null = null;

    try {
      response = await getDraftGuessWithSessionRecovery(questionId, assignmentId);
    } catch (error) {
      if (shouldReportDraftLoadFailure(error)) {
        notifier.notify(error, {
          message: 'Draft guess failed to load',
          tags: ['draft-guess'],
          context: {questionId, assignmentId, status: (error as {status?: number})?.status}
        });
      }
      this.writeData('draftLoadFailed', true);
      this.writeData('saveStatus', SaveStatus.LOAD_ERROR);
      return;
    }

    if (response === null || response.size === 0) {
      this._clearDraftGuessData();
      return;
    }

    const draftGuess = response.first();
    this._applyDraftRow(draftGuess);
    this.writeData('draftLoadFailed', false);
    this.writeData('saveStatus', SaveStatus.SAVED);

    this.afterLoadDraftGuess(draftGuess);
  }

  _applyDraftRow(draftGuess: Map<string, unknown>): string | null {
    const id = draftGuess.get('id', null) as string | null;
    this.writeData('draftId', id);
    this.writeData('draftGuess', draftGuess);
    this.writeData('timeElapsed', draftGuess.get('time_elapsed', 0));
    this.writeData('lastDraftTime', draftGuess.get('updated_at', null));
    return id;
  }

  _loadGuess(guess) {
    this._loadQuestion();
    this.writeData((store) =>
      store
        .set('isGuessCorrect', guess.isCorrect())
        .set('hasResponse', true)
        .set('pointsEarned', guess.getPointsEarned())
    );
  }

  _loadQuestion(question = this.readData('__question')) {
    /**
     * @todo (sort of): The following assignment operation looks first to the "new-school" V2/V3 way
     * of fetching question rubrics (via the meta) vs. the "old-school" V1 top-level attribute. This
     * view is getting replaced by the new practice view soon, so I am content with foregoing making
     * changes to the queries and extensions.
     */
    const rubric = question.getIn(['meta', 'question_rubric'], Map());

    this.writeData((store) =>
      store
        .set('rubric', rubric)
        .set('solutionText', question.getSolutionText())
        .set('pointsPossible', question.getPoints())
    );
  }

  /**
   * True when a save must be held back because the draft failed to load on a *first* load and
   * the server still holds an answer. That failed load blanks the editor, so persisting what is
   * in it would overwrite the answer; a reload is the only way to resume saving. A save is not
   * held when the draft id is already known (a re-load blip can be PATCHed safely), when the
   * server has no answer, or when its only row is empty — in which case that row is adopted so
   * the save patches it instead of creating a duplicate.
   */
  async shouldHoldSaveForFailedLoad(assignmentId: string | null): Promise<boolean> {
    if (!this.readData('draftLoadFailed')) {
      return false;
    }
    if (this.readData('draftId') !== null) {
      return false;
    }
    try {
      const response = await getDraftGuessWithSessionRecovery(
        this.readData('questionId'),
        assignmentId
      );
      if (response.size === 0) {
        return false;
      }
      if (isGuessOrDraftGuessEmpty(response.first())) {
        this._applyDraftRow(response.first());
        return false;
      }
      return true;
    } catch (error) {
      if (error instanceof SessionEndedDuringDraftLoadError) {
        return true;
      }
      if (!isTransportError(error)) {
        notifier.notify(error, {
          message: 'Draft guess failed to load before a save',
          tags: ['draft-guess'],
          context: {
            questionId: this.readData('questionId'),
            assignmentId,
            status: (error as {status?: number})?.status
          }
        });
      }
      return false;
    }
  }

  /**
   * Saves run one at a time per store: a save that overlaps another would read a stale
   * draft id and create a second row for the question.
   */
  saveDraftGuess(): Promise<void> {
    const run = this.saveChain.then(() => this._saveDraftGuess());
    this.saveChain = run.catch(() => {});
    return run;
  }

  async _saveDraftGuess() {
    if (QuestionTypeStore.shouldNotSaveDraft()) {
      return;
    }

    const assignmentId = QuestionTypeStore.getAssignmentId();
    const questionId = this.readData('questionId');

    if (await this.shouldHoldSaveForFailedLoad(assignmentId)) {
      // Nothing is pending — a reload, not a save, is what resolves this.
      this.writeData('hasPendingDebouncedSaveDraftGuess', false);
      return;
    }

    const draftId = this.readData('draftId');
    const draftGuessContent =
      typeof this.buildDraftGuessContent === 'function'
        ? this.buildDraftGuessContent()
        : this.buildGuessContent();
    const payload = {timeElapsed: this.buildTimeElapsed(), content: draftGuessContent};
    const request =
      draftId !== null
        ? patchDraftGuess(draftId, payload)
        : saveDraftGuess(payload, questionId, assignmentId);

    this.writeData('saveStatus', SaveStatus.SAVING);
    this.writeData('draftGuessPromise', request);

    let response;

    try {
      response = await this.getDraftGuessPromise();
    } catch (error) {
      // todo: add error toast that there was an error saving?
      // or should this function take an error callback.
      // add error to save status
      this.writeData('hasPendingDebouncedSaveDraftGuess', false);
      this.writeData('saveStatus', SaveStatus.SAVE_ERROR);
      logger.error('Error saving draft guess: %o', error);
      return;
    }

    this.writeData('hasPendingDebouncedSaveDraftGuess', false);
    this.startTimer();

    const updatedAt = response.get('updated_at', new Date().toISOString());
    const timeElapsed = response.get('time_elapsed', 0);

    if (this.readData('draftId') === null) {
      this.writeData('draftId', response.get('id', null));
    }
    this.writeData('draftGuess', response);
    this.writeData('draftLoadFailed', false);
    this.writeData('saveStatus', SaveStatus.SAVED);
    this.writeData('lastDraftTime', updatedAt);
    this.writeData('timeElapsed', timeElapsed);
  }

  static shouldNotSaveDraft() {
    const assignmentId = this.getAssignmentId();
    const isStudent = sessionStore.isStudent();
    const userId = sessionStore.getUserId();

    return assignmentId === null || !isStudent || userId === undefined;
  }

  startTimer() {
    this.writeData('editStartTime', new Date());
  }
}

export default QuestionTypeStore;
